import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GlobalContext } from "../../../Context/GlobalContext";
import { Toaster, toast } from "react-hot-toast";
import { Download, FileArchive, FileText, QrCode, RefreshCcw } from "lucide-react";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import QRCode from "qrcode";

import useDining from "../../Services/OrderServices/DiningServices/useDining";

/**
 * NOTE:
 * - Uses `api` from GlobalContext for auth.
 * - Fetches tables by `clientId` param.
 * - Three formats:
 *    1) 10×15 cm sticker (PNG or PDF grid),
 *    2) 5×10 cm sticker (PNG or PDF grid),
 *    3) QR Only (ultra-high-res PNG).
 */

export default function AdminQrCode() {
  const {getRestaurantTables}=useDining();

  const { clientId, clientName } = useParams();
  const { api } = useContext(GlobalContext);

  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);

  const [genPDF_10x15, setGenPDF_10x15] = useState(false);
  const [genZIP_10x15, setZIP_10x15] = useState(false);
  const [genPDF_5x10, setGenPDF_5x10] = useState(false);
  const [genZIP_5x10, setZIP_5x10] = useState(false);
  const [genZIP_QROnly, setZIP_QROnly] = useState(false);

const fetchTables = async () => {
  if (!clientId) return;

  setLoading(true);

  try {
    const result = await getRestaurantTables(clientId);

    if (result.success) {
      setTables(result.data);
    } else {
      setTables([]);
      toast.error(result.message);
    }
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  /* ---------- QR generators ---------- */

  // Ultra-high-res QR ONLY (square)
  const genQROnlyDataURL = async (whatsappNumber, message) => {
    const link = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message || "")}`;
    try {
      const dataURL = await QRCode.toDataURL(link, {
        errorCorrectionLevel: "H",
        margin: 1,
        scale: 100, // ~3000px
      });
      // draw onto fixed 3000×3000 canvas for consistency
      const img = new Image();
      img.src = dataURL;
      return await new Promise((resolve) => {
        img.onload = () => {
          const side = 3000;
          const canvas = document.createElement("canvas");
          canvas.width = side;
          canvas.height = side;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, side, side);
          resolve(canvas.toDataURL("image/png", 1.0));
        };
      });
    } catch (err) {
      console.error("QR only generation failed", err);
      return null;
    }
  };

  // 5×10 cm sticker (landscape) — returns PNG dataURL
  const genSticker_5x10 = async ({ tableNo, retaurentName, whatsappNumber, message }) => {
    const link = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message || "")}`;
    try {
      const canvasWidth = 1181; // ~10 cm @ 300dpi
      const canvasHeight = 591; // ~5 cm @ 300dpi
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // background gradient
      const bg = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      bg.addColorStop(0, "#FFD54F");
      bg.addColorStop(1, "#FF9800");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // inner card
      const pad = 30;
      const cardX = pad, cardY = pad;
      const cardW = canvasWidth - pad * 2;
      const cardH = canvasHeight - pad * 2;
      const r = 30;

      const cardG = ctx.createLinearGradient(0, cardY, 0, cardY + cardH);
      cardG.addColorStop(0, "#fff8e1");
      cardG.addColorStop(1, "#ffe0b2");

      ctx.fillStyle = cardG;
      ctx.beginPath();
      ctx.moveTo(cardX + r, cardY);
      ctx.lineTo(cardX + cardW - r, cardY);
      ctx.quadraticCurveTo(cardX + cardW, cardY, cardX + cardW, cardY + r);
      ctx.lineTo(cardX + cardW, cardY + cardH - r);
      ctx.quadraticCurveTo(cardX + cardW, cardY + cardH, cardX + cardW - r, cardY + cardH);
      ctx.lineTo(cardX + r, cardY + cardH);
      ctx.quadraticCurveTo(cardX, cardY + cardH, cardX, cardY + cardH - r);
      ctx.lineTo(cardX, cardY + r);
      ctx.quadraticCurveTo(cardX, cardY, cardX + r, cardY);
      ctx.closePath();
      ctx.fill();

      // QR
      const qrURL = await QRCode.toDataURL(link, { errorCorrectionLevel: "H", margin: 1, scale: 8 });
      const qrImg = new Image();
      qrImg.src = qrURL;

      return await new Promise((resolve) => {
        qrImg.onload = () => {
          const midX = canvasWidth / 2;
          // right side QR
          const qrSize = 400;
          const qrX = midX + (midX - qrSize) / 2;
          const qrY = cardY + (cardH - qrSize) / 2;
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

          // left texts
          ctx.textAlign = "center";
          ctx.fillStyle = "#d84315";
          ctx.font = "bold 48px 'Poppins', sans-serif";
          ctx.fillText(retaurentName || "Restaurant", midX / 2, cardY + 120);

          ctx.fillStyle = "#ef6c00";
          ctx.font = "bold 72px 'Segoe UI', sans-serif";
          ctx.fillText(`TABLE ${tableNo}`, midX / 2, cardY + 200);

          ctx.fillStyle = "#4e342e";
          ctx.font = "500 48px 'Segoe UI', sans-serif";
          ctx.fillText("Scan to Order", midX / 2, cardY + 280);
          ctx.font = "500 40px 'Segoe UI', sans-serif";
          ctx.fillText("on WhatsApp", midX / 2, cardY + 330);

          resolve(canvas.toDataURL("image/png", 1.0));
        };
      });
    } catch (err) {
      console.error("5×10 sticker generation failed", err);
      return null;
    }
  };

  // 10×15 cm sticker (portrait) — returns PNG dataURL
  const genSticker_10x15 = async ({ tableNo, retaurentName, whatsappNumber, message }) => {
    const link = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message || "")}`;
    try {
      // approx @300dpi: 10x15 cm ≈ 1181×1772 but we’ll scale up a bit for crispness
      const canvasWidth = 1200;
      const canvasHeight = 1800;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // gradient bg
      const outer = ctx.createLinearGradient(0, 0, 0, canvasHeight);
      outer.addColorStop(0, "#FFD54F");
      outer.addColorStop(1, "#FF9800");
      ctx.fillStyle = outer;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      // inner card
      const pad = 24;
      const cardX = pad, cardY = pad;
      const cardW = canvasWidth - pad * 2;
      const cardH = canvasHeight - pad * 2;
      const r = 24;

      const cardG = ctx.createLinearGradient(0, cardY, 0, cardY + cardH);
      cardG.addColorStop(0, "#fff8e1");
      cardG.addColorStop(1, "#ffe0b2");
      ctx.fillStyle = cardG;

      ctx.beginPath();
      ctx.moveTo(cardX + r, cardY);
      ctx.lineTo(cardX + cardW - r, cardY);
      ctx.quadraticCurveTo(cardX + cardW, cardY, cardX + cardW, cardY + r);
      ctx.lineTo(cardX + cardW, cardY + cardH - r);
      ctx.quadraticCurveTo(cardX + cardW, cardY + cardH, cardX + cardW - r, cardY + cardH);
      ctx.lineTo(cardX + r, cardY + cardH);
      ctx.quadraticCurveTo(cardX, cardY + cardH, cardX, cardY + cardH - r);
      ctx.lineTo(cardX, cardY + r);
      ctx.quadraticCurveTo(cardX, cardY, cardX + r, cardY);
      ctx.closePath();
      ctx.fill();

      // QR
      const qrURL = await QRCode.toDataURL(link, { errorCorrectionLevel: "H", margin: 1, scale: 30 });
      const qrImg = new Image();
      qrImg.src = qrURL;

      return await new Promise((resolve) => {
        qrImg.onload = () => {
          // logo/title area
          ctx.textAlign = "center";
          ctx.fillStyle = "#d84315";
          ctx.font = "bold 60px 'Poppins', sans-serif";
          ctx.fillText(retaurentName || "Restaurant", canvasWidth / 2, cardY + 150);

          // QR center
          const qrSize = 720;
          const qrX = (canvasWidth - qrSize) / 2;
          const qrY = cardY + 260;
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

          // captions
          ctx.fillStyle = "#4e342e";
          ctx.font = "600 44px 'Segoe UI', sans-serif";
          ctx.fillText("Scan to Order Your Food", canvasWidth / 2, qrY + qrSize + 70);

          // table box
          const tbY = qrY + qrSize + 120;
          ctx.fillStyle = "#ef6c00";
          ctx.fillRect(80, tbY, canvasWidth - 160, 100);

          ctx.fillStyle = "#fff";
          ctx.font = "bold 56px 'Segoe UI', sans-serif";
          ctx.fillText(`TABLE ${tableNo}`, canvasWidth / 2, tbY + 68);

          resolve(canvas.toDataURL("image/png", 1.0));
        };
      });
    } catch (err) {
      console.error("10×15 sticker generation failed", err);
      return null;
    }
  };

  /* ---------- Single downloads ---------- */

  const downloadSingle = (dataUrl, filename) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    a.click();
  };

  const handleOne_10x15 = async (t) => {
    const dataUrl = await genSticker_10x15(t);
    if (dataUrl) downloadSingle(dataUrl, `table-${t.tableNo}-10x15.png`);
  };

  const handleOne_5x10 = async (t) => {
    const dataUrl = await genSticker_5x10(t);
    if (dataUrl) downloadSingle(dataUrl, `table-${t.tableNo}-5x10.png`);
  };

  const handleOne_QR = async (t) => {
    const dataUrl = await genQROnlyDataURL(t.whatsappNumber, t.message);
    if (dataUrl) downloadSingle(dataUrl, `table-${t.tableNo}-qr.png`);
  };

  /* ---------- Bulk PDF / ZIP ---------- */

  const bulkPDF_10x15 = async () => {
    setGenPDF_10x15(true);
    try {
      const pdf = new jsPDF("portrait", "mm", "a4");
      const w = 96, h = 144; // each sticker size
      const pageW = 210, pageH = 297;
      const cols = 2, rows = 2;
      const mX = (pageW - cols * w) / (cols + 1);
      const mY = (pageH - rows * h) / (rows + 1);

      const images = await Promise.all(
        tables.map((t) => genSticker_10x15(t))
      );
      const valid = images.filter(Boolean);

      let index = 0;
      for (let i = 0; i < valid.length; i++) {
        const col = index % cols;
        const row = Math.floor(index / cols) % rows;
        const x = mX + col * (w + mX);
        const y = mY + row * (h + mY);

        pdf.addImage(valid[i], "PNG", x, y, w, h);
        index++;

        if (index % (cols * rows) === 0 && i !== valid.length - 1) {
          pdf.addPage();
        }
      }
      pdf.save(`Dining_QR_10x15_${clientName || clientId}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate PDF");
    } finally {
      setGenPDF_10x15(false);
    }
  };

  const bulkPDF_5x10 = async () => {
    setGenPDF_5x10(true);
    try {
      const pdf = new jsPDF("portrait", "mm", "a4");
      const w = 100, h = 50;
      const pageW = 210, pageH = 297;
      const cols = 2, rows = 5;
      const mX = (pageW - cols * w) / (cols + 1);
      const mY = (pageH - rows * h) / (rows + 1);

      const images = await Promise.all(
        tables.map((t) => genSticker_5x10(t))
      );
      const valid = images.filter(Boolean);

      let index = 0;
      for (let i = 0; i < valid.length; i++) {
        const col = index % cols;
        const row = Math.floor(index / cols) % rows;
        const x = mX + col * (w + mX);
        const y = mY + row * (h + mY);

        pdf.addImage(valid[i], "PNG", x, y, w, h);
        index++;

        if (index % (cols * rows) === 0 && i !== valid.length - 1) {
          pdf.addPage();
        }
      }
      pdf.save(`Dining_QR_5x10_${clientName || clientId}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate PDF");
    } finally {
      setGenPDF_5x10(false);
    }
  };

  const bulkZIP_10x15 = async () => {
    setZIP_10x15(true);
    try {
      const zip = new JSZip();
      const files = await Promise.all(
        tables.map(async (t) => {
          const d = await genSticker_10x15(t);
          return d ? { name: `table-${t.tableNo}-10x15.png`, data: d } : null;
        })
      );
      for (const f of files.filter(Boolean)) {
        zip.file(f.name, f.data.split(",")[1], { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `Dining_QR_10x15_${clientName || clientId}.zip`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to build ZIP");
    } finally {
      setZIP_10x15(false);
    }
  };

  const bulkZIP_5x10 = async () => {
    setZIP_5x10(true);
    try {
      const zip = new JSZip();
      const files = await Promise.all(
        tables.map(async (t) => {
          const d = await genSticker_5x10(t);
          return d ? { name: `table-${t.tableNo}-5x10.png`, data: d } : null;
        })
      );
      for (const f of files.filter(Boolean)) {
        zip.file(f.name, f.data.split(",")[1], { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `Dining_QR_5x10_${clientName || clientId}.zip`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to build ZIP");
    } finally {
      setZIP_5x10(false);
    }
  };

  const bulkZIP_QROnly = async () => {
    setZIP_QROnly(true);
    try {
      const zip = new JSZip();
      const files = await Promise.all(
        tables.map(async (t) => {
          const d = await genQROnlyDataURL(t.whatsappNumber, t.message);
          return d ? { name: `table-${t.tableNo}-qr.png`, data: d } : null;
        })
      );
      for (const f of files.filter(Boolean)) {
        zip.file(f.name, f.data.split(",")[1], { base64: true });
      }
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `Dining_QR_Only_${clientName || clientId}.zip`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to build ZIP");
    } finally {
      setZIP_QROnly(false);
    }
  };

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Download QR Codes {clientName ? `of ${clientName}` : ""}</h1>
          <p className="text-sm text-gray-500">Bulk export or per-table stickers</p>
        </div>
        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
          onClick={fetchTables}
          disabled={loading}
        >
          <RefreshCcw size={16} />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Bulk Options */}
      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-1 text-sm font-semibold">QR 10×15 cm</div>
          <p className="text-xs text-gray-600">
            Large sticker for standees. Includes name, table number, QR & prompt.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-xl bg-[#01C37D] px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
              onClick={bulkPDF_10x15}
              disabled={genPDF_10x15 || !tables.length}
            >
              <FileText size={16} />
              {genPDF_10x15 ? "Generating…" : "Generate PDF"}
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
              onClick={bulkZIP_10x15}
              disabled={genZIP_10x15 || !tables.length}
            >
              <FileArchive size={16} />
              {genZIP_10x15 ? "Generating…" : "Download ZIP"}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-1 text-sm font-semibold">QR 5×10 cm</div>
          <p className="text-xs text-gray-600">
            Compact table sticker. Clean info with high readability.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-xl bg-[#01C37D] px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
              onClick={bulkPDF_5x10}
              disabled={genPDF_5x10 || !tables.length}
            >
              <FileText size={16} />
              {genPDF_5x10 ? "Generating…" : "Generate PDF"}
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
              onClick={bulkZIP_5x10}
              disabled={genZIP_5x10 || !tables.length}
            >
              <FileArchive size={16} />
              {genZIP_5x10 ? "Generating…" : "Download ZIP"}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-1 text-sm font-semibold">Only QR</div>
          <p className="text-xs text-gray-600">
            Ultra-HD square QR only. For menus, posters, or custom layouts.
          </p>
          <div className="mt-3">
            <button
              className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
              onClick={bulkZIP_QROnly}
              disabled={genZIP_QROnly || !tables.length}
            >
              <Download size={16} />
              {genZIP_QROnly ? "Generating…" : "Download ZIP"}
            </button>
          </div>
        </div>
      </div>

      {/* Table-wise actions */}
      <div className="mt-5 rounded-2xl border bg-white p-3">
        <div className="mb-2 text-sm font-semibold">Table-wise QR Downloads</div>
        {loading && tables.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">Loading…</div>
        ) : tables.length === 0 ? (
          <div className="py-6 text-center text-sm text-gray-500">No tables found.</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            {tables.map((t) => (
              <div key={t.id || t.tableNo} className="rounded-2xl border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm">
                    Table <b>#{t.tableNo}</b>
                  </div>
                  <div className="rounded-full border bg-gray-50 px-2 py-0.5 text-[11px]">
                    {String(t.status) !== "Open" ? "Booked" : "Available"}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    className="inline-flex items-center gap-1 rounded-xl border px-3 py-1.5 text-xs hover:bg-gray-50"
                    onClick={() => handleOne_10x15(t)}
                  >
                    <QrCode size={14} /> 10×15
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-xl border px-3 py-1.5 text-xs hover:bg-gray-50"
                    onClick={() => handleOne_5x10(t)}
                  >
                    <QrCode size={14} /> 5×10
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-xl border px-3 py-1.5 text-xs hover:bg-gray-50"
                    onClick={() => handleOne_QR(t)}
                  >
                    <QrCode size={14} /> QR Only
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
